/**
 * Trades Poster — Licences, Badges & Tickets
 * Source catalogue workflow (list sort, validation, save helpers).
 */

import {
  LBT_ALL_TRADES_CATEGORY_ID,
  LBT_ITEM_ID_PREFIX,
  LBT_ITEM_TYPE_OPTIONS,
  LBT_ITEM_TYPE_SORT_ORDER,
  cloneLbtSourceItem,
  emptyLbtSourceItem,
  getLbtItemTypeLabel,
  resolveDefaultFormIdForLbtType,
} from './licensingCredentialRecords';
import { getFormById } from './formInventoryConfigurationConfig';
import { emitRuleSaveHistory } from './changeHistoryEmitters';
import { CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';

export const LBT_SOURCE_ROUTE = '/admin/licences-badges-trade-tickets';

export const LBT_TYPE_FILTER_OPTIONS = [
  { value: 'All', label: 'All' },
  ...LBT_ITEM_TYPE_OPTIONS,
];

export const DEFAULT_ACTOR = 'Saim Sahi';

export const SAVE_MESSAGES = {
  success: 'Record saved successfully.',
  failure: 'Could not save. Review the highlighted fields and try again.',
};

export const VALIDATION_MESSAGES = {
  itemTypeRequired: 'Select a type.',
  itemNameRequired: 'Enter a name.',
  descriptionRequired: 'Enter a description.',
  tradeCategoryRequired: 'Select a trade category or All Trades.',
  iconRequired: 'Add an icon.',
  iconUnsupported: 'The selected icon file is not supported.',
  duplicate: 'This name already exists for this type.',
  formRequired: 'Select a form.',
};

export const trimItemName = (value) => (value || '').trim().replace(/\s+/g, ' ');

export const normItemName = (value) => trimItemName(value).toLowerCase();

export function formatLbtSourceListDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      if (typeof value === 'string' && value.trim()) return value;
      return '—';
    }
    const datePart = d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timePart = d.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${datePart}, ${timePart}`;
  } catch {
    return value;
  }
}

export function formatLbtSourceAuditDate(date = new Date()) {
  return date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function sortLbtSourceItems(items = []) {
  return [...items].sort((a, b) => {
    const typeA = LBT_ITEM_TYPE_SORT_ORDER[a.itemType] ?? 99;
    const typeB = LBT_ITEM_TYPE_SORT_ORDER[b.itemType] ?? 99;
    if (typeA !== typeB) return typeA - typeB;
    return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true });
  });
}

export function matchesLbtSourceSearch(item, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [item?.id, item?.itemName].join(' ').toLowerCase();
  return haystack.includes(q);
}

export function matchesLbtSourceTypeFilter(item, typeFilter = 'All') {
  if (!typeFilter || typeFilter === 'All') return true;
  return item?.itemType === typeFilter;
}

export function isValidItemName(value) {
  return Boolean(trimItemName(value));
}

export function findDuplicateSourceItem(items, { itemType, itemName, excludeId }) {
  const name = normItemName(itemName);
  const type = itemType || '';
  if (!name || !type) return null;
  return (
    (items || []).find(
      (row) =>
        row.id !== excludeId &&
        row.itemType === type &&
        normItemName(row.itemName) === name,
    ) ?? null
  );
}

export function formInputsFromInventoryForm(forms = [], formId = '') {
  const form = getFormById(forms, formId);
  return (form?.fields || [])
    .filter((field) => field?.status !== 'Inactive')
    .map((field) => ({
      id: field.id,
      fieldLabel: field.fieldLabel || '',
      fieldControl: field.controlType || '',
      required: field.baseRequirement === 'Required' ? 'Required' : 'Optional',
    }));
}

export function collectLbtSourceFieldErrors(
  formData,
  { items = [], isEdit = false, tradeCategories = [] } = {},
) {
  const errors = {};
  if (!formData?.itemType) errors.itemType = VALIDATION_MESSAGES.itemTypeRequired;

  const name = trimItemName(formData?.itemName);
  if (!name) errors.itemName = VALIDATION_MESSAGES.itemNameRequired;

  if (!String(formData?.description || '').trim()) {
    errors.description = VALIDATION_MESSAGES.descriptionRequired;
  }

  const categoryId = formData?.tradeCategoryId || '';
  const knownCategory =
    categoryId === LBT_ALL_TRADES_CATEGORY_ID ||
    (tradeCategories || []).some((row) => row.id === categoryId);
  if (!categoryId || !knownCategory) {
    errors.tradeCategoryId = VALIDATION_MESSAGES.tradeCategoryRequired;
  }

  if (!(formData?.iconFileName || formData?.iconUrl)) {
    errors.icon = VALIDATION_MESSAGES.iconRequired;
  }

  if (!formData?.formId) {
    errors.formId = VALIDATION_MESSAGES.formRequired;
  }

  if (!errors.itemType && !errors.itemName) {
    const dup = findDuplicateSourceItem(items, {
      itemType: formData.itemType,
      itemName: name,
      excludeId: isEdit ? formData.id : undefined,
    });
    if (dup) errors.itemName = VALIDATION_MESSAGES.duplicate;
  }

  return errors;
}

export function nextLbtSourceItemId(items = [], itemType) {
  const prefix = LBT_ITEM_ID_PREFIX[itemType];
  if (!prefix) return '';

  const pattern = new RegExp(`^${prefix}-(\\d+)$`, 'i');
  const nums = (items || [])
    .map((row) => {
      const match = pattern.exec(row.id || '');
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `${prefix}-${String(next).padStart(4, '0')}`;
}

export function buildIconLabel(itemName) {
  const name = trimItemName(itemName);
  return name ? `${name} icon` : '';
}

export function mergeLbtSourceAfterSave(items, formData, { isNew = false, actor = DEFAULT_ACTOR, forms = [] } = {}) {
  const list = items || [];
  const now = new Date().toISOString();
  const name = trimItemName(formData.itemName);
  const payload = {
    ...cloneLbtSourceItem(formData),
    itemName: name,
    description: String(formData.description || '').trim(),
    itemStatus: 'Active',
    formId: formData.formId || resolveDefaultFormIdForLbtType(formData.itemType, forms),
    tradeCategoryId: formData.tradeCategoryId || '',
    iconUrl: formData.iconUrl || '',
    iconFileName: formData.iconFileName || '',
    iconLabel: formData.iconLabel || formData.iconFileName || buildIconLabel(name),
    formInputs: formInputsFromInventoryForm(forms, formData.formId || resolveDefaultFormIdForLbtType(formData.itemType, forms)),
  };

  const existing = payload.id ? list.find((row) => row.id === payload.id) : null;
  if (existing) {
    const next = {
      ...existing,
      ...payload,
      id: existing.id,
      itemType: existing.itemType,
      createdOn: existing.createdOn,
      createdBy: existing.createdBy,
    };
    if (!sourceItemFormHasChanges(next, existing)) {
      return sortLbtSourceItems(list);
    }
    next.lastUpdatedOn = now;
    next.lastUpdatedBy = actor;
    try {
      emitRuleSaveHistory({
        isNew: false,
        previous: existing,
        next,
        module: 'Licences, Badges & Tickets',
        sourceModuleRef: 'compliance_items_management',
        changedItem: next.itemName || next.id,
        itemId: next.id,
        changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
        actor,
        stamp: now,
        compareKeys: ['itemName', 'description', 'itemType', 'formId', 'tradeCategoryId', 'itemStatus'],
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
    return sortLbtSourceItems(list.map((row) => (row.id === next.id ? next : row)));
  }

  if (!isNew) {
    return sortLbtSourceItems(list);
  }

  const duplicate = findDuplicateSourceItem(list, {
    itemType: formData.itemType,
    itemName: name,
  });
  if (duplicate) {
    return sortLbtSourceItems(list);
  }

  payload.id = nextLbtSourceItemId(list, formData.itemType);
  payload.createdOn = now;
  payload.createdBy = actor;
  payload.lastUpdatedOn = now;
  payload.lastUpdatedBy = actor;
  try {
    emitRuleSaveHistory({
      isNew: true,
      previous: null,
      next: payload,
      module: 'Licences, Badges & Tickets',
      sourceModuleRef: 'compliance_items_management',
      changedItem: payload.itemName || payload.id,
      itemId: payload.id,
      changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      actor,
      stamp: now,
      compareKeys: ['itemName', 'description', 'itemType', 'formId', 'tradeCategoryId', 'itemStatus'],
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return sortLbtSourceItems([...list, payload]);
}

export function sourceItemMatchesUrlId(id, urlId) {
  if (!id || !urlId) return false;
  return decodeURIComponent(String(urlId)) === String(id);
}

export function sourceItemFormHasChanges(formData, baseline) {
  const left = formData || emptyLbtSourceItem();
  const right = baseline || emptyLbtSourceItem();
  return (
    (left.itemType || '') !== (right.itemType || '') ||
    trimItemName(left.itemName) !== trimItemName(right.itemName) ||
    String(left.description || '').trim() !== String(right.description || '').trim() ||
    (left.tradeCategoryId || '') !== (right.tradeCategoryId || '') ||
    (left.iconUrl || '') !== (right.iconUrl || '') ||
    (left.iconFileName || '') !== (right.iconFileName || '') ||
    (left.formId || '') !== (right.formId || '')
  );
}

export { getLbtItemTypeLabel };
